const BASE_TARGET_TAG = '<base target="_blank">';
const VIDEO_THUMBNAIL_SELECTOR = '.js-gjs-video-thumbnail';
// Iframes that already carry the delegated 'load' listener, so
// enableInlineVideoPlayback stays idempotent per element without holding the
// node from GC.
const loadBoundIframes = new WeakSet();

// Video ids from the common YouTube / Vimeo URL shapes.
const YOUTUBE_ID = /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([\w-]{11})/i;
const VIMEO_ID = /vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d+)/i;

// DEF-3235: a click inside a preview iframe (srcdoc) navigates the iframe
// itself. Non-video links (buttons etc.) point at pages that may refuse framing
// (X-Frame-Options), so forcing a base target of _blank makes them open in a
// new tab instead of breaking the preview. Video thumbnails are handled
// separately by enableInlineVideoPlayback. Preview-only: the stored and sent
// email HTML are not touched.
export const injectPreviewBaseTarget = (html) => {
    if (!html) {
        return html;
    }

    // Detect against a copy with HTML comments blanked out (same length, so match
    // indices still line up with `html`): a <base>/<head> that only lives inside a
    // comment must not count. <head(\s...)?> matches <head>/<head lang=…> but not
    // <header>.
    const probe = html.replace(/<!--[\s\S]*?-->/g, (m) => ' '.repeat(m.length));

    if (/<base[^>]*\btarget=/i.test(probe)) {
        return html;
    }

    // Prefer inside <head>; else right after <html>; else after a leading doctype. A
    // <base> placed before <body> is hoisted into the implied head by the parser. Only a
    // true fragment (no doctype/html/head) falls through to prepend, so we never emit
    // content ahead of a <!DOCTYPE>, which would force the preview into quirks mode.
    const insertAfter = (re) => {
        const m = probe.match(re);
        if (!m) {
            return null;
        }
        const end = m.index + m[0].length;
        return html.slice(0, end) + BASE_TARGET_TAG + html.slice(end);
    };

    return (
        insertAfter(/<head(\s[^>]*)?>/i) ||
        insertAfter(/<html(\s[^>]*)?>/i) ||
        insertAfter(/^\s*<!doctype[^>]*>/i) ||
        `${BASE_TARGET_TAG}${html}`
    );
};

// Build an embeddable player straight from a YouTube/Vimeo URL. Both expose
// stable, framable player URLs (youtube.com/embed, player.vimeo.com), so we skip
// the /oembed/get round-trip entirely: that server call is rate-limited (HTTP 429)
// and slow, which is why videos only played inline intermittently. Returns null
// for any other host, and the caller then lets <base target="_blank"> open it in
// a new tab.
export const buildProviderEmbed = (url) => {
    if (!url) {
        return null;
    }

    const youtube = url.match(YOUTUBE_ID);
    if (youtube) {
        return `<iframe src="https://www.youtube.com/embed/${youtube[1]}?autoplay=1" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>`;
    }

    const vimeo = url.match(VIMEO_ID);
    if (vimeo) {
        return `<iframe src="https://player.vimeo.com/video/${vimeo[1]}?autoplay=1" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
    }

    return null;
};

// Swap a video thumbnail anchor for the provider embed player. Inline styles
// give a 16:9 responsive frame because the preview srcdoc is the customer email
// document and does not carry the app's embed-responsive CSS.
export const injectInlineVideo = (anchorEl, code) => {
    if (!anchorEl || !code) {
        return;
    }

    const wrapper = anchorEl.ownerDocument.createElement('div');
    wrapper.style.cssText = 'position:relative;padding-bottom:56.25%;height:0;overflow:hidden;';
    wrapper.innerHTML = code;

    const embed = wrapper.querySelector('iframe, embed, object, video');
    if (embed) {
        embed.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;';
    }

    anchorEl.replaceWith(wrapper);
};

// A click on a video thumbnail inside the preview. YouTube/Vimeo play inline via a
// locally-built embed (synchronous, no network). Any other host falls through untouched
// so the injected <base target="_blank"> opens it in a new tab: a gesture-driven native
// navigation that is never popup-blocked (unlike an async window.open from a callback).
export const handleVideoThumbnailClick = (event) => {
    const anchor = event.target && event.target.closest ? event.target.closest('a') : null;
    if (!anchor || !anchor.querySelector(VIDEO_THUMBNAIL_SELECTOR)) {
        return;
    }

    // Let modified / non-primary clicks do their native thing (new tab via base target).
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) {
        return;
    }

    const url = anchor.getAttribute('href');
    if (!url || url === '#') {
        // Dead placeholder link: block it so the base target can't open a blank tab.
        event.preventDefault();
        return;
    }

    const embed = buildProviderEmbed(url);
    if (embed) {
        event.preventDefault();
        injectInlineVideo(anchor, embed);
    }
};

// Delegate clicks in a preview iframe so a video thumbnail plays inline instead
// of navigating the iframe to the (unframeable) provider watch page.
export const enableInlineVideoPlayback = (iframe) => {
    if (!iframe) {
        return;
    }

    const bind = () => {
        const doc = iframe.contentDocument;
        // document.open() (append-styles path) reuses the Document object but erases
        // its listeners, so re-add unconditionally on every load. addEventListener
        // dedupes the identical handler, so repeat binds on one document are a no-op.
        if (doc) {
            doc.addEventListener('click', handleVideoThumbnailClick);
        }
    };

    // Attach the load listener once per iframe element (the node persists across
    // dialog opens, so re-adding would leak a handler each time). Listeners on the
    // element live in the parent document and are not erased by document.open().
    if (!loadBoundIframes.has(iframe)) {
        loadBoundIframes.add(iframe);
        iframe.addEventListener('load', bind);
    }

    bind();
};

// Blank a preview iframe so its document, and any inline autoplay video it holds,
// is torn down. el-dialog keeps the iframe in the DOM on close, so without this the
// nested YouTube/Vimeo embed keeps playing (audio) behind the closed dialog.
export const blankPreviewIframe = (iframe) => {
    if (!iframe) {
        return;
    }

    iframe.removeAttribute('src');
    iframe.setAttribute('srcdoc', '');
};
